import loadImage from 'blueimp-load-image'

// 縦 or 横の最大サイズ
const MAX_LENGTH = 1600

// base64サイズ上限（バイト）
const MAX_BITE = 1024 * 500

/**
 * 画像リサイズの結果
 * @typedef {Object} resizeResult
 * @property {string} dataUrl
 */

/**
 * 画像リサイズ
 * @param {File} file リサイズしたいファイル
 * @returns {Promise<resizeResult>} リサイズ済みのファイル（Base64）
 */
export default function resizeImage(file) {
  return new Promise(function(resolve, reject) {
    if (!validate(file)) {
      reject(new Error('Not a image.'))
    }

    loadImage.parseMetaData(file, data => {
      // スマホで撮った写真の回転を防ぐ
      const options = data.exif
        ? { orientation: data.exif.get('Orientation') }
        : {}

      const onLoad = image => {
        const dataUrl = imageToDataUrl(image)
        resolve({ dataUrl })
      }

      loadImage(file, onLoad, options)
    })
  })
}

/**
 * ファイルは画像？
 * @param {File} file File
 * @returns {boolean} Validate result
 */
function validate(file) {
  if (!file) {
    return false
  }

  return file.type && file.type.indexOf('image') >= 0
}

/**
 * Image -> Data URL
 * @param {Image} image Image
 * @returns {string} Data URL
 */
function imageToDataUrl(image) {
  // Image -> Canvas
  const canvas = imageToCanvas(image)
  // Canvas -> Data URL
  return canvasToDataUrl(canvas)
}

/**
 * Image -> Canvas
 * @param {Image} image Image
 * @returns {HTMLCanvasElement} Canvas Element
 */
function imageToCanvas(image) {
  const canvas = document.createElement('canvas')

  const { width, height } = getImageSize(image)
  canvas.width = width
  canvas.height = height

  const ctx = canvas.getContext('2d')
  ctx.drawImage(image, 0, 0, width, height)

  return canvas
}

/**
 * Canvas -> Data URL
 * @param {HTMLCanvasElement} canvas Canvas Element
 * @returns {string} Data URL
 */
function canvasToDataUrl(canvas) {
  let dataUrl = canvas.toDataURL('image/jpeg')
  let ratio = MAX_BITE / dataUrl.length

  // MAX_BITE 以下になるまで圧縮し続ける
  while (dataUrl.length > MAX_BITE) {
    dataUrl = canvas.toDataURL('image/jpeg', ratio)
    ratio -= 0.05
  }

  return dataUrl
}

/**
 * @typedef {Object} Size
 * @property {number} width
 * @property {number} height
 */

/**
 * 縦横が最大値に収まるまで縮小した画像サイズを計算する
 * @param {Image} image
 * @returns {Size}
 */
function getImageSize(image) {
  const tempWidth = image.width
  const tempHeight = image.height

  // 横長 && 最大値より大きい
  if (tempWidth > tempHeight) {
    if (tempWidth > MAX_LENGTH) {
      const height = tempHeight * (MAX_LENGTH / tempWidth)
      return { height, width: MAX_LENGTH }
    }
  }

  // 縦長 && 最大値より大きい
  if (tempHeight > MAX_LENGTH) {
    const width = tempWidth * (MAX_LENGTH / tempHeight)
    return { height: MAX_LENGTH, width }
  }

  // 縦横が最大値以下
  return { height: tempHeight, width: tempWidth }
}
